import React, { useState, useEffect, Suspense, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { Scene } from "./Scene.tsx";
import { ResponsiveCamera } from "./ResponsiveCamera.tsx";
import { sectionIds, sections, Section } from "./constants.ts";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";

export default function Ceres() {
  const [activeNav, setActiveNav] = useState<Section>("");
  const [coords, setCoords] = useState("1.2874° N, 103.84899° E");
  const [loaded, setLoaded] = useState(false);
  const [fov, setFov] = useState(100);
  const [scrollAngle, setScrollAngle] = useState(0);
  const [navVisible, setNavVisible] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const hideNav = params.get("hideNav") === "true";
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const navTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showHint, setShowHint] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);


  useEffect(() => {  
    const params = new URLSearchParams(window.location.search)

    const paramFov = params.get("fov")
    const fovFromUrl = paramFov ? parseFloat(paramFov) : null

    const THRESHOLD = 810
    const LARGE_FOV = 120
    const DEFAULT_FOV = 100

    const updateFov = () => {
      if (fovFromUrl && !isNaN(fovFromUrl)) {
        setFov(fovFromUrl)
      } else {
        const width = window.innerWidth
        setFov(width < THRESHOLD ? LARGE_FOV : DEFAULT_FOV)
      }
    }

    updateFov()
    window.addEventListener("resize", updateFov)
    return () => window.removeEventListener("resize", updateFov)
  }, [])

  // Handle user activity to show/hide navigation
  useEffect(() => {
    const handleActivity = () => {
      setNavVisible(true);
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
      navTimeoutRef.current = setTimeout(() => setNavVisible(false), 4000);
    };
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("touchstart", handleActivity);
    window.addEventListener("wheel", handleActivity, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("wheel", handleActivity);
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
    };
  }, []);

  // Show hint if user has not interacted
  useEffect(() => {
    if (isUserInteracting && !hasInteracted) {
      setHasInteracted(true);
      setShowHint(false);
    }
  }, [isUserInteracting, hasInteracted]);

  // Handle scroll events to update scroll angle
  useEffect(() => {
    let ticking = false;

    const updateScroll = (delta: number) => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => {
          setScrollAngle((prev) => prev + delta * 0.002);
          ticking = false;
        });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setShowHint(false);
      }
      updateScroll(e.deltaY);
    };

    window.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [hasInteracted]);

  // Show hint after 500ms if navigation is not visible and user has not interacted
  useEffect(() => {
    if (!navVisible && hasInteracted) {
      const timeout = setTimeout(() => {
        setShowHint(true);
        setHasInteracted(false); // allow future interactions to hide it again
      }, 500); // delay to avoid flashing immediately

      return () => clearTimeout(timeout);
    }
  }, [navVisible, hasInteracted]);

  // Redirect to active navigation section after 3 seconds of inactivity
  useEffect(() => {
    if (isUserInteracting || activeNav === "") return;
    const timeout = setTimeout(() => {
      window.parent.postMessage(
        { type: "ceresRedirect", payload: { section: activeNav } },
        "*"
      );
    }, 2500);

    return () => clearTimeout(timeout);
  }, [activeNav, isUserInteracting]);

  // Post message to parent window with updated coordinates and active navigation
  useEffect(() => {
    window.parent.postMessage(
      { type: "ceresUpdate", payload: { coords, activeNav } },
      "*"
    );
  }, [coords, activeNav]);

  // Handle pointer events for dragging and clicking
  useEffect(() => {
    const canvas = document.querySelector("canvas");
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    let isPointerDown = false;
    let pointerDownTarget: EventTarget | null = null;
    const threshold = 5;

    const handlePointerDown = (e: PointerEvent) => {
      if (!canvas?.contains(e.target as Node)) return;
      startX = e.clientX;
      startY = e.clientY;
      isPointerDown = true;
      isDragging = false;
      pointerDownTarget = e.target;
    };


    const handlePointerMove = (e: PointerEvent) => {
      if (!isPointerDown) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (!isDragging && distance > threshold) {
        isDragging = true;
      }
    };


    const handlePointerUp = (e: PointerEvent) => {
      if (!isPointerDown) return;
      isPointerDown = false;

      // Only handle click redirects if no drag happened
      if (!isDragging && e.target === pointerDownTarget && activeNav && navVisible) {
        window.parent.postMessage(
          { type: "ceresRedirect", payload: { section: activeNav } },
          "*"
        );
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [activeNav, navVisible]);


  return (
    <div id="app-container" style={{
      pointerEvents: "auto",
      cursor: activeNav && navVisible ? "pointer" : "default",
    }}>
      <Canvas camera={{ position: [0, 0, 5], fov }} style={{ width: "100%", height: "100%" }}>
        <Suspense fallback={null}>
          <ResponsiveCamera fov={fov} loaded={loaded} />
          <Scene
            setActiveNav={setActiveNav}
            setCoords={setCoords}
            setLoaded={setLoaded}
            scrollAngle={scrollAngle}
            setScrollAngle={setScrollAngle}
            setIsUserInteracting={setIsUserInteracting}
            isUserInteracting={isUserInteracting}
          />
        </Suspense>
      </Canvas>

      {loaded && !hideNav && (
        <>
          <nav className={`main-nav ${!navVisible ? "hidden" : ""}`}>
            <div className="nav-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeNav}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0 }}
                >
                  {sectionIds[sections.indexOf(activeNav)]}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="nav-right">
              <AnimatePresence mode="wait">
                <motion.button
                  key={activeNav}
                  onClick={(e) => {
                    e.preventDefault()
                    window.parent.postMessage(
                      { type: "ceresRedirect", payload: { section: activeNav } },
                      "*"
                    )
                  }}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0 }}
                  className="nav-button"
                >
                  {activeNav}
                </motion.button>
              </AnimatePresence>
            </div>
          </nav>

          <AnimatePresence>
            {showHint && (
              <motion.div
                key="scroll-hint"
                className="scroll-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                drag / scroll to explore
              </motion.div>
            )}
          </AnimatePresence>

        </>
      )}

    </div>
  );
}